/**
 * Serviço central de notificações do sistema
 * Gerencia notificações persistentes (SidePanel) e temporárias (Toast)
 */

class NotificationService {
  constructor() {
    this.listeners = [];
    this.showToastByDefault = true; // Controla se deve mostrar toast automaticamente
  }

  /**
   * Adiciona uma notificação ao sistema
   * @param {Object} notification - Objeto de notificação
   * @param {String} notification.id - ID único da notificação
   * @param {String} notification.type - Tipo da notificação (REPORT, ALERT, PROCESS, etc.)
   * @param {String} notification.status - Status (PROCESSADO, EM_PROCESSAMENTO, ERRO, INFO, WARNING, SUCCESS)
   * @param {String} notification.title - Título da notificação
   * @param {String} notification.message - Mensagem da notificação
   * @param {String} notification.category - Categoria para agrupamento (opcional)
   * @param {Object} notification.metadata - Dados adicionais (opcional)
   * @param {Array} notification.actions - Ações disponíveis (opcional)
   * @param {Number} notification.timestamp - Timestamp (gerado automaticamente se não fornecido)
   * @param {Boolean} notification.showToast - Se deve mostrar toast (padrão: true)
   * @param {Boolean} notification.addToPanel - Se deve adicionar ao painel (padrão: true)
   * @param {Number} notification.duration - Duração do toast em ms (padrão: 6000)
   */
  addNotification(notification) {
    // Validação básica
    if (!notification.id) {
      notification.id = `notification_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    if (!notification.timestamp) {
      notification.timestamp = Date.now();
    }

    if (!notification.type) {
      notification.type = 'GENERIC';
    }

    if (!notification.status) {
      notification.status = 'INFO';
    }

    // Configurações padrão
    const showToast = notification.showToast !== false && this.showToastByDefault;
    const addToPanel = notification.addToPanel !== false;

    // Adicionar ao painel persistente (localStorage)
    if (addToPanel) {
      this._addToPersistentStorage(notification);

      // Despachar evento para NotificationBell/SidePanel
      const event = new CustomEvent('system-notification', {
        detail: notification
      });
      window.dispatchEvent(event);

      // Emitir evento via Vue root (para NotificationSidePanel)
      if (window.app && window.app.$root) {
        window.app.$root.$emit('notifications-updated');
      }
    }

    // Mostrar toast temporário
    if (showToast) {
      this._showToast(notification);
    }

    // Notificar listeners customizados
    this.listeners.forEach(listener => listener(notification));

    return notification.id;
  }

  /**
   * Adiciona notificação ao localStorage
   */
  _addToPersistentStorage(notification) {
    try {
      const stored = localStorage.getItem('systemNotifications');
      let notifications = stored ? JSON.parse(stored) : [];

      // Preparar notificação para serialização (remover handlers)
      const notificationToStore = {
        ...notification,
        actions: notification.actions ? notification.actions.map(a => ({
          label: a.label,
          icon: a.icon,
          type: a.type
          // handler não é salvo - será reconstruído ao carregar
        })) : []
      };

      // Verifica se já existe e atualiza, ou adiciona nova
      const existingIndex = notifications.findIndex(n => n.id === notification.id);
      if (existingIndex > -1) {
        notifications[existingIndex] = { ...notifications[existingIndex], ...notificationToStore };
      } else {
        notifications.unshift(notificationToStore);
      }

      // Mantém no máximo 50 notificações
      if (notifications.length > 50) {
        notifications = notifications.slice(0, 50);
      }

      localStorage.setItem('systemNotifications', JSON.stringify(notifications));
    } catch (error) {
      // Erro ao salvar notificação - log suprimido
    }
  }

  /**
   * Mostra toast temporário
   */
  _showToast(notification) {
    if (window.app && window.app.$root) {
      window.app.$root.$emit('show-toast', {
        id: notification.id,
        type: notification.type,
        status: notification.status,
        title: notification.title,
        message: notification.message,
        metadata: notification.metadata,
        duration: notification.duration || 6000
      });
    }
  }

  /**
   * Remove uma notificação
   */
  removeNotification(notificationId) {
    // Remove do localStorage
    try {
      const stored = localStorage.getItem('systemNotifications');
      if (stored) {
        let notifications = JSON.parse(stored);
        notifications = notifications.filter(n => n.id !== notificationId);
        localStorage.setItem('systemNotifications', JSON.stringify(notifications));
      }
    } catch (error) {
      // Erro ao remover notificação - log suprimido
    }

    // Emite eventos
    const event = new CustomEvent('system-notification-remove', {
      detail: { id: notificationId }
    });
    window.dispatchEvent(event);

    if (window.app && window.app.$root) {
      window.app.$root.$emit('notifications-updated');
    }
  }

  /**
   * Limpa todas as notificações
   */
  clearAll() {
    // Limpa localStorage
    try {
      localStorage.removeItem('systemNotifications');
    } catch (error) {
      // Erro ao limpar notificações - log suprimido
    }

    // Emite eventos
    const event = new CustomEvent('system-notification-clear-all');
    window.dispatchEvent(event);

    if (window.app && window.app.$root) {
      window.app.$root.$emit('notifications-updated');
    }
  }

  /**
   * Registra listener customizado
   */
  addListener(callback) {
    this.listeners.push(callback);
  }

  /**
   * Remove listener
   */
  removeListener(callback) {
    this.listeners = this.listeners.filter(l => l !== callback);
  }
}

// Singleton
const notificationService = new NotificationService();

export default notificationService;
